/**
 * Relative "last active" formatter for provider presence.
 *
 * When a provider is online/active the UI keeps its live online indicator; this
 * helper is only used for the NOT-online case, turning the time since they were
 * last active into a friendly relative string instead of the literal "Offline".
 *
 * Buckets (time since last activity):
 * - under 1 min       → "active just now"
 * - 1–10 min          → exact minutes
 * - 11–22 min         → "active 15 minutes ago"
 * - 23–45 min         → "active 30 minutes ago"
 * - 46 min – 23 h     → nearest hour, e.g. "active 2 hours ago"
 * - 24 h – 6 d        → nearest day, e.g. "active 2 days ago"
 * - 7 d – 4 w         → nearest week, e.g. "active 2 weeks ago"
 * - more than 4 w     → "active over a month ago"
 */

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

function parseTimestamp(value: string | number | null | undefined): number | null {
  if (value == null) return null
  const ts = typeof value === 'number' ? value : Date.parse(value)
  return Number.isFinite(ts) ? ts : null
}

function withUnit(count: number, unit: 'minute' | 'hour' | 'day' | 'week'): string {
  return `${count} ${unit}${count === 1 ? '' : 's'}`
}

/**
 * Returns a relative "active …" label for the given last-active timestamp, or
 * `null` when no usable timestamp is available (callers should fall back to
 * their existing status label in that case).
 */
export function formatRelativeLastActive(
  lastActiveAt: string | number | null | undefined,
  now: number = Date.now(),
): string | null {
  const ts = parseTimestamp(lastActiveAt)
  if (ts == null) return null

  const minutes = Math.max(0, Math.floor((now - ts) / MINUTE_MS))

  if (minutes < 1) return 'active just now'
  if (minutes <= 10) return `active ${withUnit(minutes, 'minute')} ago`
  if (minutes <= 22) return 'active 15 minutes ago'
  if (minutes <= 45) return 'active 30 minutes ago'

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    const roundedHours = Math.max(1, Math.min(23, Math.round(minutes / 60)))
    return `active ${withUnit(roundedHours, 'hour')} ago`
  }

  const days = Math.floor((now - ts) / DAY_MS)
  if (days < 7) {
    const roundedDays = Math.max(1, Math.min(6, Math.round((now - ts) / DAY_MS)))
    return `active ${withUnit(roundedDays, 'day')} ago`
  }

  const weeks = Math.floor(days / 7)
  if (weeks <= 4) {
    return `active ${withUnit(Math.max(1, weeks), 'week')} ago`
  }

  return 'active over a month ago'
}
